import { HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { InfoService } from "@core-services/info.service";
import { ModalService } from "@core-services/modal.service";
import { StatsigClientService } from "@core-services/statsig/statsig.service";
import { TranslationService } from "@core-services/translation.service";
import { SocketService } from "@main-services/socket.service";
import { AssetTransaction, CryptoTransaction, GenericTransaction, SendTransaction } from "@models/transaction";
import { Network } from "@modules/+new/payments/features/crypto-deposit/domain/entities/network.model";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { SendMultichainMoneyOnWayComponent } from "@modules/main/components/step-summary/success-screens/send-multichain-money-on-way/send-multichain-money-on-way.component";
import { SendWaitingComponent } from "@modules/main/components/step-summary/success-screens/send-waiting/send-waiting.component";
import { filter } from "rxjs/operators";
import { TravelRuleWithdrawalResponse } from "../entities/travelRuleWithdrawal.model";

@Injectable()
export class MultichainSendService {
	private readonly statsigClientService = inject(StatsigClientService);

	constructor(
		private readonly socketService: SocketService,
		private readonly modalService: ModalService,
		private readonly apicallService: ApicallService,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService
	) {}

	public showSuccessScreen(
		transaction: CryptoTransaction,
		selectedNetwork: Network,
		selectedCryptocoin: PaymentsCryptocoin
	): void {
		const sub = this.socketService
			.joinRoom({
				name: "wallet_transactions",
				isPrivate: true,
				type: GenericTransaction
			})
			.pipe(
				filter((t: AssetTransaction) => t.getId() === transaction.getId()),
				filter((t: CryptoTransaction) => t.isStatus("finished") || t.isOpenInvitation() || t.isConfirmed())
			)
			.subscribe((transaction: CryptoTransaction) => {
				if (!transaction.isConfirmed()) {
					return;
				}
				waitingModal.dismiss();
				if (transaction.isOpenInvitation() || transaction.isStatus("finished")) {
					if (sub) {
						sub.unsubscribe();
					}
					this.modalService
						.create(SendMultichainMoneyOnWayComponent, { sendTransaction: transaction })
						.present();
				}
			});
		this.modalService.popAll();
		const waitingModal = this.modalService
			.create(SendWaitingComponent, {
				transaction: transaction,
				subForCancel: sub,
				isMultichain: true,
				cryptoName: selectedCryptocoin.name,
				networkName: selectedNetwork.name
			})
			.present();
	}

	public finishWithdrawal(
		sendTransaction: SendTransaction,
		walletId: string,
		network: Network,
		crypto: PaymentsCryptocoin
	): void {
		const a = new ApicallOption(CryptoTransaction, sendTransaction);
		this.apicallService.post("wallet/" + walletId + "/send", a).subscribe({
			next: (res) => {
				this.showSuccessScreen(res[0], network, crypto);
			},
			error: (error) => {
				this.infoService.setError(error);
			}
		});
	}

	public finishTravelRuleWithdrawal(
		address_book_entry_id: string,
		amount: string,
		wallet_id: string,
		network: Network,
		crypto: PaymentsCryptocoin,
		on_behalf_sender_legal_name: string,
		auth_code?: string
	): void {
		const coin_network_id = network.coin_network_id;
		const apiCallOption: ApicallOption = new ApicallOption(null, {
			address_book_entry_id,
			amount,
			wallet_id,
			coin_network_id,
			...(auth_code && { auth_code }),
			...(on_behalf_sender_legal_name && { on_behalf_sender_legal_name })
		});
		apiCallOption.headers = new HttpHeaders().set("bp-domain", "bitpanda");
		apiCallOption.apiVersion = "v2";

		this.apicallService.post("transactions/crypto/withdrawal", apiCallOption).subscribe({
			next: (apiResponse: TravelRuleWithdrawalResponse) => {
				this.showSuccessScreen(this.getTravelRuleTransaction(apiResponse), network, crypto);
			},
			error: (error) => {
				if (error.responseErrors[0].code === "user.wallet.balance.low") {
					error.responseErrors[0].title = this.translationService.translate(
						"user_wallet_low_balance_error_notification_message"
					);
				}
				this.infoService.setError(error);
			}
		});
	}

	private getTravelRuleTransaction(apiResponse: TravelRuleWithdrawalResponse): CryptoTransaction {
		const cryptoTransaction = new CryptoTransaction();
		cryptoTransaction.setStatus(apiResponse.attributes.status);
		cryptoTransaction.setAmount(+apiResponse.attributes.amount);
		cryptoTransaction.setWalletId(apiResponse.attributes.wallet_id);
		cryptoTransaction.setId(apiResponse.transaction_id);

		return cryptoTransaction;
	}
}
